import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import VintageLayout from '../components/layout/VintageLayout';
import { VersionManager } from '../components/admin/VersionManager';

interface Stats {
    users: { total: number };
    games: { active: number; lobby: number; total_memory: number; redis: any };
    system: { uptime: number; memory: any };
}

interface Room {
    id: string;
    code: string;
    name: string;
    hostId: string;
    status: string;
    playerCount: number;
    maxPlayers: number;
    createdAt: string;
}

interface User {
    _id: string;
    username: string;
    email: string;
    role: string;
    isBanned: boolean;
    createdAt: string;
}

interface Log {
    timestamp: string;
    type: string;
    message: string;
}

interface Ticket {
    id: string;
    title: string;
    description: any; // RichText
    category: string;
    severity: string; // low, medium, high, critical
    status: string; // open, in-progress, resolved, closed
    reportedBy: { username: string; email: string } | string;
    createdAt: string;
}

export const AdminPage: React.FC = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState<Stats | null>(null);
    const [rooms, setRooms] = useState<Room[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [logs, setLogs] = useState<Log[]>([]);
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [activeTab, setActiveTab] = useState<'OVERVIEW' | 'USERS' | 'LOGS' | 'DIAGNOSTICS' | 'CMS' | 'THEMES' | 'VERSIONS' | 'TICKETS'>('OVERVIEW');
    const [broadcastMsg, setBroadcastMsg] = useState('');
    const [diagnosticLogs, setDiagnosticLogs] = useState<string[]>([]);
    const [isRunningDiagnostics, setIsRunningDiagnostics] = useState(false);



    // User Management State
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // CMS State
    const [packs, setPacks] = useState<any[]>([]);
    const [selectedPack, setSelectedPack] = useState<any>(null);
    const [packTemplates, setPackTemplates] = useState<any[]>([]);
    const [isEditingPack, setIsEditingPack] = useState(false);
    const [editingPackId, setEditingPackId] = useState<string | null>(null);
    const [newPackData, setNewPackData] = useState({ name: '', description: '', isAdult: false, active: true });
    const [newTemplateText, setNewTemplateText] = useState('');
    const [newTemplateResponse, setNewTemplateResponse] = useState('');
    const [newTemplateOptions, setNewTemplateOptions] = useState<string[]>([]);
    const [isMCQMode, setIsMCQMode] = useState(false);
    const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
    const [packFilter, setPackFilter] = useState<'ALL' | 'OFFICIAL' | 'COMMUNITY'>('ALL');
    const [selectedUserForDetail, setSelectedUserForDetail] = useState<any | null>(null);

    // Import/Export State
    const [isImporting, setIsImporting] = useState(false);
    const [showImportHelp, setShowImportHelp] = useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const fetchPacks = async () => {
        try {
            const { data } = await api.get('/admin/packs');
            setPacks(data);
        } catch (error) {
            console.error('Error fetching packs:', error);
        }
    };

    const fetchPackDetails = async (packId: string) => {
        try {
            const { data } = await api.get(`/admin/packs/${packId}/templates`);
            setPackTemplates(data);
        } catch (error) {
            console.error('Error fetching pack details:', error);
        }
    };

    // Theme Management State
    const [themes, setThemes] = useState<any[]>([]);
    const [isEditingTheme, setIsEditingTheme] = useState(false);
    const [editingThemeId, setEditingThemeId] = useState<string | null>(null);
    const [themeForm, setThemeForm] = useState({
        name: '',
        isDefault: false,
        cssVariables: {
            '--bg-app': '#1a0f0a',
            '--bg-board': '#2c1810',
            '--border-color': '#8d6e63',
            '--accent-primary': '#d4af37',
            '--text-main': '#fdfbf7',
            '--token-self': '#22c55e',
            '--token-opp': '#ef4444',
            '--paper-light': '#fdfbf7',
            '--paper-dark': '#e6dfcc',
            '--ink-black': '#1a1a1a'
        }
    });

    const fetchThemes = async () => {
        try {
            const { data } = await api.get('/themes'); // Using correct public/protected endpoint
            setThemes(data);
        } catch (error) {
            console.error('Error fetching themes:', error);
        }
    };

    useEffect(() => {
        if (activeTab === 'THEMES') {
            fetchThemes();
        }
    }, [activeTab]);

    const handleSaveTheme = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (editingThemeId) {
                await api.put(`/themes/${editingThemeId}`, themeForm);
                toast.success('Thème mis à jour avec succès');
            } else {
                await api.post('/themes', themeForm);
                toast.success('Thème créé avec succès');
            }
            setIsEditingTheme(false);
            setEditingThemeId(null);
            fetchThemes();
            // Reset form
            setThemeForm({
                name: '',
                isDefault: false,
                cssVariables: {
                    '--bg-app': '#1a0f0a',
                    '--bg-board': '#2c1810',
                    '--border-color': '#8d6e63',
                    '--accent-primary': '#d4af37',
                    '--text-main': '#fdfbf7',
                    '--token-self': '#22c55e',
                    '--token-opp': '#ef4444',
                    '--paper-light': '#fdfbf7',
                    '--paper-dark': '#e6dfcc',
                    '--ink-black': '#1a1a1a'
                }
            });
        } catch (error) {
            toast.error(editingThemeId ? 'Erreur mise à jour thème' : 'Erreur création thème');
        }
    };

    const handleDeleteTheme = async (themeId: string) => {
        if (!window.confirm('Supprimer ce thème ?')) return;
        try {
            await api.delete(`/themes/${themeId}`);
            toast.success('Thème supprimé');
            fetchThemes();
        } catch (error) {
            toast.error('Erreur suppression thème');
        }
    };

    useEffect(() => {
        if (activeTab === 'CMS') {
            fetchPacks();
        }
    }, [activeTab]);

    useEffect(() => {
        if (selectedPack) {
            fetchPackDetails(selectedPack._id);
        }
    }, [selectedPack]);

    const handleCreateOrUpdatePack = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (editingPackId) {
                await api.put(`/admin/packs/${editingPackId}`, newPackData);
                toast.success('Pack mis à jour');
            } else {
                await api.post('/admin/packs', newPackData);
                toast.success('Pack créé');
            }
            setIsEditingPack(false);
            setEditingPackId(null);
            setNewPackData({ name: '', description: '', isAdult: false, active: true });
            fetchPacks();
        } catch (error) {
            toast.error('Erreur sauvegarde pack');
        }
    };

    const handleEditPack = (pack: any) => {
        setNewPackData({
            name: pack.name,
            description: pack.description,
            isAdult: pack.isAdult,
            active: pack.active
        });
        setEditingPackId(pack._id);
        setIsEditingPack(true);
    };

    const handlePromotePack = async (packId: string) => {
        if (!window.confirm('Promouvoir ce pack en "Officiel" ? Il sera accessible à tous.')) return;
        try {
            await api.put(`/admin/packs/${packId}`, { isOfficial: true, isPrivate: false, ownerId: null });
            toast.success('Pack promu Officiel');
            fetchPacks();
        } catch (error) {
            toast.error('Erreur promotion pack');
        }
    };

    const handleDeletePack = async (packId: string) => {
        if (!window.confirm('Supprimer ce pack et tout son contenu ?')) {
            return;
        }
        try {
            await api.delete(`/admin/packs/${packId}`);
            toast.success('Pack supprimé');
            fetchPacks();
            if (selectedPack?._id === packId) setSelectedPack(null);
        } catch (error) {
            console.error('Delete request failed:', error);
            toast.error('Erreur suppression pack');
        }
    };

    const handleTogglePackActive = async (pack: any) => {
        try {
            await api.put(`/admin/packs/${pack._id}`, { active: !pack.active });
            toast.success('Statut mis à jour');
            fetchPacks();
        } catch (error) {
            toast.error('Erreur mise à jour');
        }
    };

    const handleExportPack = async (packId: string, packName: string) => {
        try {
            const { data } = await api.get(`/admin/packs/${packId}/export`);
            const jsonString = JSON.stringify(data, null, 2);
            const blob = new Blob([jsonString], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `pack_export_${packName.replace(/\s+/g, '_')}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            toast.success('Export réussi');
        } catch (error) {
            toast.error('Erreur export pack');
        }
    };

    const handleImportPack = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const json = JSON.parse(e.target?.result as string);
                setIsImporting(true);
                await api.post('/admin/packs/import', json);
                toast.success('Pack importé avec succès');
                fetchPacks();
            } catch (error) {
                console.error(error);
                toast.error('Erreur import pack (Format invalide ?)');
            } finally {
                setIsImporting(false);
                if (fileInputRef.current) fileInputRef.current.value = '';
            }
        };
        reader.readAsText(file);
    };

    const handleCreateOrUpdateTemplate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newTemplateText.trim()) return;

        try {
            const payload = {
                text: newTemplateText,
                response: newTemplateResponse,
                options: isMCQMode ? newTemplateOptions.filter(o => o.trim() !== '') : []
            };

            if (editingTemplateId) {
                await api.put(`/admin/templates/${editingTemplateId}`, payload);
                toast.success('Template mis à jour');
            } else {
                await api.post(`/admin/packs/${selectedPack._id}/templates`, payload);
                toast.success('Template créé');
            }
            fetchPackDetails(selectedPack._id);
            setNewTemplateText('');
            setNewTemplateResponse('');
            setNewTemplateOptions([]);
            setIsMCQMode(false);
            setEditingTemplateId(null);
        } catch (error) {
            toast.error('Erreur sauvegarde template');
        }
    };

    const handleEditTemplate = (tpl: any) => {
        setNewTemplateText(tpl.text);
        setNewTemplateResponse(tpl.response || '');
        setNewTemplateOptions(tpl.options || []);
        setIsMCQMode(!!(tpl.options && tpl.options.length > 0));
        setEditingTemplateId(tpl._id);
    };

    const handleDeleteTemplate = async (templateId: string) => {
        try {
            await api.delete(`/admin/templates/${templateId}`);
            fetchPackDetails(selectedPack._id);
            toast.success('Template supprimé');
        } catch (error) {
            toast.error('Erreur suppression template');
        }
    };

    const runDiagnostics = async () => {
        setIsRunningDiagnostics(true);
        setDiagnosticLogs(['🚀 Initialisation du diagnostic...']);

        try {
            const token = localStorage.getItem('token');

            const response = await fetch('/api/admin/diagnostics/run', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.body) return;
            const reader = response.body.getReader();
            const decoder = new TextDecoder();

            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                const text = decoder.decode(value);
                const lines = text.split('\n').filter(line => line.trim());
                setDiagnosticLogs(prev => [...prev, ...lines]);
            }
        } catch (error) {
            setDiagnosticLogs(prev => [...prev, '❌ Erreur de connexion au serveur']);
        } finally {
            setIsRunningDiagnostics(false);
        }
    };

    const runTournament = async () => {
        setIsRunningDiagnostics(true);
        setDiagnosticLogs(['🏆 Initialisation du tournoi (100 matchs)...']);

        try {
            const token = localStorage.getItem('token');

            const response = await fetch('/api/admin/tournament/run', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ count: 100 })
            });

            if (!response.body) return;
            const reader = response.body.getReader();
            const decoder = new TextDecoder();

            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                const text = decoder.decode(value);
                const lines = text.split('\n').filter(line => line.trim());
                setDiagnosticLogs(prev => [...prev, ...lines]);
            }
        } catch (error) {
            setDiagnosticLogs(prev => [...prev, '❌ Erreur de connexion au serveur']);
        } finally {
            setIsRunningDiagnostics(false);
        }
    };

    const fetchStats = async () => {
        try {
            const { data } = await api.get('/admin/stats');
            setStats(data);
            return true;
        } catch (error: any) {
            console.error('Error fetching stats:', error);
            if (error.response?.status === 401) return false;
            return true;
        }
    };

    const fetchRooms = async () => {
        try {
            const { data } = await api.get('/admin/rooms');
            setRooms(data);
            return true;
        } catch (error: any) {
            console.error('Error fetching rooms:', error);
            if (error.response?.status === 401) return false;
            return true;
        }
    };

    const fetchUsers = async () => {
        try {
            const { data } = await api.get(`/admin/users?page=${currentPage}&limit=10&search=${searchTerm}`);
            setUsers(data.users);
            setTotalPages(data.totalPages);
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    // Debounce search
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (activeTab === 'USERS') {
                setCurrentPage(1);
                fetchUsers();
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm]);

    useEffect(() => {
        if (activeTab === 'USERS') fetchUsers();
    }, [currentPage, activeTab]);

    const handleToggleBan = async (user: User) => {
        if (!window.confirm(`Voulez-vous vraiment ${user.isBanned ? 'débannir' : 'bannir'} ${user.username} ?`)) return;
        try {
            await api.patch(`/admin/users/${user._id}/ban`);
            toast.success(`Utilisateur ${user.isBanned ? 'débanni' : 'banni'}`);
            fetchUsers();
        } catch (error) {
            toast.error('Erreur lors du changement de statut');
        }
    };

    const handleToggleRole = async (user: User) => {
        const newRole = user.role === 'ADMIN' ? 'USER' : 'ADMIN';
        if (!window.confirm(`Passer ${user.username} en ${newRole} ?`)) return;
        try {
            await api.patch(`/admin/users/${user._id}/role`, { role: newRole });
            toast.success(`Rôle mis à jour : ${newRole}`);
            fetchUsers();
        } catch (error) {
            toast.error('Erreur lors du changement de rôle');
        }
    };

    const fetchLogs = async () => {
        try {
            const { data } = await api.get('/admin/logs');
            setLogs(data);
            return true;
        } catch (error: any) {
            console.error('Error fetching logs:', error);
            if (error.response?.status === 401) return false;
            return true;
        }
    };

    const fetchTickets = async () => {
        try {
            const { data } = await api.get('/admin/tickets?limit=50');
            setTickets(data.docs || []);
        } catch (error) {
            toast.error('Erreur chargement tickets');
        }
    };

    const handleUpdateTicket = async (ticketId: string, updates: any) => {
        try {
            await api.put(`/admin/tickets/${ticketId}`, updates);
            toast.success('Ticket mis à jour');
            fetchTickets();
        } catch (error) {
            toast.error('Erreur mise à jour ticket');
        }
    };

    useEffect(() => {
        fetchStats();
        fetchRooms();
        if (activeTab === 'USERS') fetchUsers();
        if (activeTab === 'LOGS') fetchLogs();
        if (activeTab === 'TICKETS') fetchTickets();

        const interval = setInterval(async () => {
            const statsOk = await fetchStats();
            const roomsOk = await fetchRooms();
            let logsOk = true;
            if (activeTab === 'LOGS') logsOk = await fetchLogs(); // Auto-refresh logs

            if (!statsOk || !roomsOk || !logsOk) {
                console.warn('Authentication failed, stopping polling interval.');
                clearInterval(interval);
            }
        }, 30000);

        return () => clearInterval(interval);
    }, [activeTab]);

    const handleDeleteRoom = async (roomId: string) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette salle ?')) return;
        try {
            await api.delete(`/admin/rooms/${roomId}`);
            toast.success('Salle supprimée');
            fetchRooms();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const handleDeleteUser = async (userId: string) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) return;
        try {
            await api.delete(`/admin/users/${userId}`);
            toast.success('Utilisateur supprimé');
            fetchUsers();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const handleBroadcast = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!broadcastMsg.trim()) return;
        try {
            await api.post('/admin/broadcast', { message: broadcastMsg });
            toast.success('Message diffusé');
            setBroadcastMsg('');
        } catch (error) {
            toast.error('Erreur lors de la diffusion');
        }
    };

    const handleOpenCms = async () => {
        try {
            toast.loading('Connexion au CMS...', { id: 'cms-login' });
            const { data } = await api.get('/admin/cms-token');
            // Payload lit 'payload-token' dans localStorage
            const cmsWindow = window.open('', '_blank');
            if (cmsWindow) {
                cmsWindow.document.write(`
                    <html><body><script>
                        localStorage.setItem('payload-token', '${data.token}');
                        window.location.href = '${data.cmsUrl}';
                    <\/script></body></html>
                `);
            }
            toast.success('Ouverture du CMS...', { id: 'cms-login' });
        } catch (error) {
            toast.error('Erreur connexion CMS', { id: 'cms-login' });
        }
    };

    return (
        <VintageLayout>
            <div className="min-h-screen text-cream-100 p-6 font-sans selection:bg-gold-500/30">
                <div className="max-w-7xl mx-auto">
                    <header className="flex justify-between items-center mb-8 border-b border-navy-800 pb-6">
                        <div className="flex items-center gap-3">
                            <img
                                src="/assets/logo-text.png"
                                alt="Réminiscence"
                                className="h-12 w-auto brightness-0 invert opacity-90 hover:opacity-100 transition-opacity"
                            />
                            <div>
                                <h1 className="text-3xl font-serif font-bold text-gold-400">Bureau de Contrôle</h1>
                                <p className="text-navy-400 text-sm uppercase tracking-widest">Administration Système</p>
                            </div>
                        </div>
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="px-6 py-2 border border-navy-600 hover:border-gold-500 hover:text-gold-400 rounded-lg transition-all duration-300 flex items-center gap-2"
                        >
                            <span>← Retour aux Archives</span>
                        </button>
                    </header>

                    {/* Tabs */}
                    <div className="flex space-x-2 mb-8 bg-navy-900/50 p-1 rounded-xl border border-navy-800 inline-flex">
                        <button
                            onClick={() => setActiveTab('OVERVIEW')}
                            className={`px-6 py-2 rounded-lg transition-all duration-300 font-medium ${activeTab === 'OVERVIEW' ? 'bg-gold-500 text-navy-900 shadow-lg' : 'text-navy-400 hover:text-cream-100 hover:bg-navy-800'}`}
                        >
                            Vue d'ensemble
                        </button>
                        <button
                            onClick={() => setActiveTab('USERS')}
                            className={`px-6 py-2 rounded-lg transition-all duration-300 font-medium ${activeTab === 'USERS' ? 'bg-gold-500 text-navy-900 shadow-lg' : 'text-navy-400 hover:text-cream-100 hover:bg-navy-800'}`}
                        >
                            Joueurs
                        </button>
                        <button
                            onClick={() => setActiveTab('LOGS')}
                            className={`px-6 py-2 rounded-lg transition-all duration-300 font-medium ${activeTab === 'LOGS' ? 'bg-gold-500 text-navy-900 shadow-lg' : 'text-navy-400 hover:text-cream-100 hover:bg-navy-800'}`}
                        >
                            Journal Système
                        </button>
                        <button
                            onClick={() => setActiveTab('DIAGNOSTICS')}
                            className={`px-6 py-2 rounded-lg transition-all duration-300 font-medium ${activeTab === 'DIAGNOSTICS' ? 'bg-gold-500 text-navy-900 shadow-lg' : 'text-navy-400 hover:text-cream-100 hover:bg-navy-800'}`}
                        >
                            Diagnostics
                        </button>
                        <button
                            onClick={() => setActiveTab('CMS')}
                            className={`px-6 py-2 rounded-lg transition-all duration-300 font-medium ${activeTab === 'CMS' ? 'bg-gold-500 text-navy-900 shadow-lg' : 'text-navy-400 hover:text-cream-100 hover:bg-navy-800'}`}
                        >
                            CMS / Contenu
                        </button>
                        <button
                            onClick={() => setActiveTab('THEMES')}
                            className={`px-6 py-2 rounded-lg transition-all duration-300 font-medium ${activeTab === 'THEMES' ? 'bg-gold-500 text-navy-900 shadow-lg' : 'text-navy-400 hover:text-cream-100 hover:bg-navy-800'}`}
                        >
                            Thèmes
                        </button>
                        <button
                            onClick={() => setActiveTab('VERSIONS')}
                            className={`px-6 py-2 rounded-lg transition-all duration-300 font-medium ${activeTab === 'VERSIONS' ? 'bg-gold-500 text-navy-900 shadow-lg' : 'text-navy-400 hover:text-cream-100 hover:bg-navy-800'}`}
                        >
                            Versioning & Sessions
                        </button>
                        <button
                            onClick={() => setActiveTab('TICKETS')}
                            className={`px-6 py-2 rounded-lg transition-all duration-300 font-medium ${activeTab === 'TICKETS' ? 'bg-gold-500 text-navy-900 shadow-lg' : 'text-navy-400 hover:text-cream-100 hover:bg-navy-800'}`}
                        >
                            Tickets
                        </button>
                    </div>

                    <main className="animate-fade-in-up">
                        {activeTab === 'OVERVIEW' && (
                            <div className="space-y-8">
                                {/* Stats Cards */}
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                                    <div className="card-navy border-l-4 border-l-gold-500">
                                        <h3 className="text-gold-400/70 text-xs font-bold uppercase tracking-wider mb-2">Utilisateurs</h3>
                                        <p className="text-4xl font-serif font-bold text-white">{stats?.users.total || 0}</p>
                                    </div>
                                    <div className="card-navy border-l-4 border-l-emerald-500">
                                        <h3 className="text-emerald-400/70 text-xs font-bold uppercase tracking-wider mb-2">Parties Actives</h3>
                                        <p className="text-4xl font-serif font-bold text-emerald-400">{stats?.games.active || 0}</p>
                                    </div>
                                    <div className="card-navy border-l-4 border-l-blue-500">
                                        <h3 className="text-blue-400/70 text-xs font-bold uppercase tracking-wider mb-2">En Lobby</h3>
                                        <p className="text-4xl font-serif font-bold text-blue-400">{stats?.games.lobby || 0}</p>
                                    </div>
                                    <div className="card-navy border-l-4 border-l-purple-500">
                                        <h3 className="text-purple-400/70 text-xs font-bold uppercase tracking-wider mb-2">Uptime</h3>
                                        <p className="text-2xl font-serif font-bold text-purple-400 mt-2">{Math.floor(stats?.system.uptime || 0)}s</p>
                                    </div>
                                </div>

                                {/* Broadcast */}
                                <div className="card-vintage border-2 border-gold-500/30">
                                    <h3 className="text-xl font-serif font-bold text-navy-900 mb-4 flex items-center gap-2">
                                        <span className="text-gold-600">📢</span> Diffusion Message Global
                                    </h3>
                                    <form onSubmit={handleBroadcast} className="flex gap-4">
                                        <input
                                            type="text"
                                            value={broadcastMsg}
                                            onChange={(e) => setBroadcastMsg(e.target.value)}
                                            placeholder="Message à envoyer à tous les investigateurs..."
                                            className="flex-1 bg-cream-50 border-2 border-navy-200 rounded-lg px-4 py-3 text-navy-900 focus:outline-none focus:border-gold-500 placeholder-navy-300"
                                        />
                                        <button
                                            type="submit"
                                            className="btn-vintage px-8"
                                        >
                                            Envoyer
                                        </button>
                                    </form>
                                </div>

                                {/* Rooms List */}
                                <div className="bg-navy-900 border border-navy-700 rounded-xl overflow-hidden shadow-xl">
                                    <div className="p-6 border-b border-navy-800 flex justify-between items-center">
                                        <h3 className="text-xl font-serif font-bold text-gold-400">Salles de Jeu ({rooms.length})</h3>
                                    </div>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left">
                                            <thead className="bg-navy-950 text-navy-400 uppercase text-xs tracking-wider">
                                                <tr>
                                                    <th className="px-6 py-4 font-medium">Code</th>
                                                    <th className="px-6 py-4 font-medium">Nom</th>
                                                    <th className="px-6 py-4 font-medium">Statut</th>
                                                    <th className="px-6 py-4 font-medium">Joueurs</th>
                                                    <th className="px-6 py-4 font-medium">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-navy-800">
                                                {rooms.map(room => (
                                                    <tr key={room.id} className="hover:bg-navy-800/50 transition-colors">
                                                        <td className="px-6 py-4 font-mono text-gold-400">{room.code}</td>
                                                        <td className="px-6 py-4 font-medium text-cream-200">{room.name}</td>
                                                        <td className="px-6 py-4">
                                                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${room.status === 'PLAYING' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'}`}>
                                                                {room.status}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 text-navy-300">{room.playerCount}/{room.maxPlayers}</td>
                                                        <td className="px-6 py-4">
                                                            <button
                                                                onClick={() => handleDeleteRoom(room.id)}
                                                                className="text-red-400 hover:text-red-300 transition-colors text-sm font-medium hover:underline"
                                                            >
                                                                Supprimer
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'USERS' && (
                            <div className="bg-navy-900 border border-navy-700 rounded-xl overflow-hidden shadow-xl">
                                <div className="p-6 border-b border-navy-800 flex justify-between items-center">
                                    <h3 className="text-xl font-serif font-bold text-gold-400">Joueurs ({stats?.users.total || 0})</h3>
                                    <div className="flex gap-4">
                                        <input
                                            type="text"
                                            placeholder="Rechercher un joueur..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="bg-navy-950 border border-navy-700 rounded-lg px-4 py-2 text-cream-100 focus:border-gold-500 outline-none"
                                        />
                                    </div>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left">
                                        <thead className="bg-navy-950 text-navy-400 uppercase text-xs tracking-wider">
                                            <tr>
                                                <th className="px-6 py-4 font-medium">Pseudo</th>
                                                <th className="px-6 py-4 font-medium">Email</th>
                                                <th className="px-6 py-4 font-medium">Rôle</th>
                                                <th className="px-6 py-4 font-medium">Statut</th>
                                                <th className="px-6 py-4 font-medium">Date Inscription</th>
                                                <th className="px-6 py-4 font-medium">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-navy-800">
                                            {users.map(user => (
                                                <tr key={user._id} className={`hover:bg-navy-800/50 transition-colors ${user.isBanned ? 'bg-red-900/10' : ''}`}>
                                                    <td className="px-6 py-4 font-bold text-cream-100">
                                                        {user.username}
                                                        {user.isBanned && <span className="ml-2 text-xs text-red-500 font-bold">(BANNI)</span>}
                                                    </td>
                                                    <td className="px-6 py-4 text-navy-300">{user.email || '-'}</td>
                                                    <td className="px-6 py-4">
                                                        <button
                                                            onClick={() => handleToggleRole(user)}
                                                            className={`px-3 py-1 rounded-full text-xs font-bold cursor-pointer hover:opacity-80 ${user.role === 'ADMIN' ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' : 'bg-navy-700 text-navy-300 border border-navy-600'}`}
                                                        >
                                                            {user.role}
                                                        </button>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${user.isBanned ? 'bg-red-500/20 text-red-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                                                            {user.isBanned ? 'Banni' : 'Actif'}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-navy-300">{new Date(user.createdAt).toLocaleDateString()}</td>
                                                    <td className="px-6 py-4 flex gap-3">
                                                        <button
                                                            onClick={() => setSelectedUserForDetail(user)}
                                                            className="text-blue-400 hover:text-blue-300 transition-colors text-sm font-medium hover:underline"
                                                        >
                                                            Détails
                                                        </button>
                                                        <button
                                                            onClick={() => handleToggleBan(user)}
                                                            className={`${user.isBanned ? 'text-emerald-400 hover:text-emerald-300' : 'text-amber-400 hover:text-amber-300'} transition-colors text-sm font-medium hover:underline`}
                                                        >
                                                            {user.isBanned ? 'Débannir' : 'Bannir'}
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteUser(user._id)}
                                                            className="text-red-400 hover:text-red-300 transition-colors text-sm font-medium hover:underline"
                                                        >
                                                            Supprimer
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {/* Pagination */}
                                <div className="p-4 border-t border-navy-800 flex justify-between items-center bg-navy-950/50">
                                    <span className="text-sm text-navy-400">Page {currentPage} sur {totalPages}</span>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                            disabled={currentPage === 1}
                                            className="px-3 py-1 rounded border border-navy-700 text-navy-300 disabled:opacity-50 hover:bg-navy-800"
                                        >
                                            Précédent
                                        </button>
                                        <button
                                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                            disabled={currentPage === totalPages}
                                            className="px-3 py-1 rounded border border-navy-700 text-navy-300 disabled:opacity-50 hover:bg-navy-800"
                                        >
                                            Suivant
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* User Detail Modal */}
                        {selectedUserForDetail && (
                            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                                <div className="bg-navy-900 border border-gold-500/30 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
                                    <div className="p-6 border-b border-navy-800 flex justify-between items-center bg-navy-950/50">
                                        <h3 className="text-2xl font-serif font-bold text-gold-400">Fiche Joueur</h3>
                                        <button onClick={() => setSelectedUserForDetail(null)} className="text-navy-400 hover:text-cream-100 text-xl">✕</button>
                                    </div>
                                    <div className="p-6 space-y-8">
                                        {/* 1. Edit Profile */}
                                        <section>
                                            <h4 className="text-lg font-bold text-cream-100 mb-4 border-b border-navy-700 pb-2">👤 Informations Personnelles</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-xs font-bold text-navy-400 mb-1">Pseudo</label>
                                                    <input
                                                        type="text"
                                                        value={selectedUserForDetail.username}
                                                        onChange={(e) => setSelectedUserForDetail({ ...selectedUserForDetail, username: e.target.value })}
                                                        className="w-full bg-navy-950 border border-navy-700 rounded px-3 py-2 text-cream-100 focus:border-gold-500 outline-none"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-navy-400 mb-1">Email</label>
                                                    <input
                                                        type="email"
                                                        value={selectedUserForDetail.email || ''}
                                                        onChange={(e) => setSelectedUserForDetail({ ...selectedUserForDetail, email: e.target.value })}
                                                        className="w-full bg-navy-950 border border-navy-700 rounded px-3 py-2 text-cream-100 focus:border-gold-500 outline-none"
                                                    />
                                                </div>
                                            </div>
                                            <button
                                                onClick={async () => {
                                                    try {
                                                        await api.put(`/admin/users/${selectedUserForDetail._id}`, {
                                                            username: selectedUserForDetail.username,
                                                            email: selectedUserForDetail.email
                                                        });
                                                        toast.success('Profil mis à jour');
                                                        fetchUsers(); // Refresh list
                                                    } catch (error) {
                                                        toast.error('Erreur lors de la mise à jour');
                                                    }
                                                }}
                                                className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded font-bold text-sm transition-colors"
                                            >
                                                Enregistrer les modifications
                                            </button>
                                        </section>

                                        {/* 2. Security / Password Reset */}
                                        <section>
                                            <h4 className="text-lg font-bold text-cream-100 mb-4 border-b border-navy-700 pb-2">🔐 Sécurité</h4>
                                            <div className="bg-navy-950 p-4 rounded border border-navy-800">
                                                <p className="text-sm text-navy-300 mb-3">
                                                    Envoyez un email contenant un lien unique pour permettre à l'utilisateur de réinitialiser son mot de passe.
                                                    Ce lien est valide pendant 1 heure.
                                                </p>
                                                <button
                                                    onClick={async () => {
                                                        try {
                                                            const res = await api.post(`/admin/users/${selectedUserForDetail._id}/reset-link`);
                                                            if (res.data.link) {
                                                                // Fallback if backend returns a link (no email configured or user has no email)
                                                                const link = res.data.link;
                                                                navigator.clipboard.writeText(link);
                                                                toast.success('Lien copié (pas d\'email envoyé) !');
                                                            } else {
                                                                toast.success('Email de réinitialisation envoyé !');
                                                            }
                                                        } catch (error) {
                                                            toast.error('Erreur lors de l\'envoi de l\'email');
                                                        }
                                                    }}
                                                    className="px-4 py-2 border border-gold-500 text-gold-400 hover:bg-gold-500/10 rounded font-bold text-sm transition-colors flex items-center gap-2"
                                                >
                                                    📧 Envoyer l'email de réinitialisation
                                                </button>
                                            </div>
                                        </section>

                                        {/* 3. User Packs */}
                                        <section>
                                            <h4 className="text-lg font-bold text-cream-100 mb-4 border-b border-navy-700 pb-2">📦 Packs Créés</h4>
                                            <div className="space-y-2 max-h-60 overflow-y-auto">
                                                {packs.filter(p => p.ownerId && p.ownerId._id === selectedUserForDetail._id).length === 0 ? (
                                                    <p className="text-navy-500 italic">Aucun pack créé.</p>
                                                ) : (
                                                    packs.filter(p => p.ownerId && p.ownerId._id === selectedUserForDetail._id).map(pack => (
                                                        <div key={pack._id} className="flex justify-between items-center bg-navy-950 p-3 rounded border border-navy-800">
                                                            <div>
                                                                <span className="font-bold text-cream-200">{pack.name}</span>
                                                                <span className={`ml-2 text-xs px-2 py-0.5 rounded ${pack.active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                                                                    {pack.active ? 'Actif' : 'Inactif'}
                                                                </span>
                                                            </div>
                                                            <div className="flex gap-2">
                                                                <button
                                                                    onClick={() => handleTogglePackActive(pack)}
                                                                    className="text-xs text-navy-400 hover:text-cream-100 underline"
                                                                >
                                                                    {pack.active ? 'Désactiver' : 'Activer'}
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDeletePack(pack._id)}
                                                                    className="text-xs text-red-400 hover:text-red-300 underline"
                                                                >
                                                                    Supprimer
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        </section>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'LOGS' && (
                            <div className="bg-navy-900 border border-navy-700 rounded-xl overflow-hidden shadow-xl flex flex-col h-[700px]">
                                <div className="p-4 border-b border-navy-800 flex justify-between items-center bg-navy-950">
                                    <h3 className="text-lg font-mono font-bold text-gold-400 flex items-center gap-2">
                                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                                        Logs Serveur (Temps Réel)
                                    </h3>
                                    <button onClick={fetchLogs} className="text-xs text-navy-400 hover:text-gold-400 border border-navy-700 px-3 py-1 rounded hover:border-gold-500 transition-all">
                                        Rafraîchir
                                    </button>
                                </div>
                                <div className="flex-1 overflow-y-auto p-4 space-y-1 font-mono text-sm bg-black/30">
                                    {logs.length === 0 ? (
                                        <div className="flex items-center justify-center h-full text-navy-600 italic">
                                            Aucun événement enregistré dans les archives...
                                        </div>
                                    ) : (
                                        logs.map((log, index) => (
                                            <div key={index} className="flex gap-4 hover:bg-white/5 p-2 rounded transition-colors border-l-2 border-transparent hover:border-gold-500/50">
                                                <span className="text-navy-500 shrink-0 w-24 text-xs pt-1">
                                                    {new Date(log.timestamp).toLocaleTimeString()}
                                                </span>
                                                <span className={`font-bold shrink-0 w-20 text-xs pt-1 ${log.type === 'ERROR' ? 'text-red-500' :
                                                    log.type === 'GAME' ? 'text-emerald-500' :
                                                        'text-blue-400'
                                                    }`}>
                                                    [{log.type}]
                                                </span>
                                                <span className="text-cream-200 break-all opacity-90">
                                                    {log.message}
                                                </span>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        )}

                        {activeTab === 'DIAGNOSTICS' && (
                            <div className="space-y-6">
                                <div className="card-vintage border-2 border-gold-500/30">
                                    <div className="flex justify-between items-center mb-6">
                                        <div>
                                            <h3 className="text-xl font-serif font-bold text-navy-900 flex items-center gap-2">
                                                <span className="text-gold-600">🩺</span> Diagnostic Système Automatisé
                                            </h3>
                                            <p className="text-navy-600 text-sm mt-1">
                                                Exécutez une suite de tests E2E (End-to-End) simulant des joueurs virtuels pour vérifier l'intégrité du jeu.
                                            </p>
                                        </div>
                                        <div className="flex gap-3">
                                            {diagnosticLogs.length > 0 && (
                                                <button
                                                    onClick={() => {
                                                        const text = diagnosticLogs.join('\n');
                                                        navigator.clipboard.writeText(text);
                                                        toast.success('Logs copiés dans le presse-papier');
                                                    }}
                                                    className="px-4 py-2 border border-navy-300 text-navy-600 hover:bg-navy-100 rounded-lg transition-all flex items-center gap-2"
                                                >
                                                    📋 Copier
                                                </button>
                                            )}
                                            <button
                                                onClick={() => runTournament()}
                                                disabled={isRunningDiagnostics}
                                                className={`px-6 py-3 border border-purple-500 text-purple-600 hover:bg-purple-50 rounded-lg transition-all flex items-center gap-2 ${isRunningDiagnostics ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            >
                                                🏆 Tournoi (100)
                                            </button>
                                            <button
                                                onClick={runDiagnostics}
                                                disabled={isRunningDiagnostics}
                                                className={`btn-vintage px-8 py-3 flex items-center gap-2 ${isRunningDiagnostics ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            >
                                                {isRunningDiagnostics ? (
                                                    <>
                                                        <span className="animate-spin">⚙️</span> Exécution en cours...
                                                    </>
                                                ) : (
                                                    <>
                                                        <span>▶️</span> Lancer l'Audit
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="bg-black rounded-lg border border-navy-800 p-4 h-[500px] overflow-y-auto font-mono text-sm shadow-inner">
                                        {diagnosticLogs.length === 0 ? (
                                            <div className="h-full flex flex-col items-center justify-center text-navy-600 opacity-50">
                                                <span className="text-4xl mb-4">🖥️</span>
                                                <p>Prêt à lancer les diagnostics...</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-1">
                                                {diagnosticLogs.map((line, i) => (
                                                    <div key={i} className={`${line.includes('SUCCESS') || line.includes('PASSED') ? 'text-emerald-400' :
                                                        line.includes('FAILED') || line.includes('ERROR') ? 'text-red-400' :
                                                            line.includes('WARNING') ? 'text-amber-400' :
                                                                line.includes('SCENARIO') ? 'text-gold-400 font-bold mt-4 border-b border-gold-500/30 pb-1' :
                                                                    'text-cream-200'
                                                        }`}>
                                                        {line}
                                                    </div>
                                                ))}
                                                <div id="log-end" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'CMS' && (
                            <div className="space-y-6">
                                {/* Payload CMS Link Banner */}
                                <div className="bg-gradient-to-r from-navy-900 to-navy-800 border border-gold-500/30 rounded-xl p-6 shadow-lg flex flex-col md:flex-row justify-between items-center gap-4">
                                    <div>
                                        <h3 className="text-xl font-bold text-gold-400 mb-2 flex items-center gap-2">
                                            <span className="text-2xl">🎓</span> Gestion des Tests & Feedbacks
                                        </h3>
                                        <p className="text-cream-200 text-sm max-w-2xl">
                                            Les <strong>Plans de Test</strong>, les <strong>Tickets</strong> et les missions des bêta-testeurs se gèrent
                                            sur une interface dédiée (Payload Admin).
                                        </p>
                                    </div>
                                    <button
                                        onClick={handleOpenCms}
                                        className="btn-vintage px-6 py-3 flex items-center gap-2 no-underline hover:text-navy-900 whitespace-nowrap shadow-gold-glow transform hover:scale-105 transition-transform"
                                    >
                                        <span>🚀</span> Accéder au CMS
                                    </button>
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                    {/* Left Column: Packs List */}
                                    <div className="lg:col-span-1 space-y-6">
                                        <div className="bg-navy-900 border border-navy-700 rounded-xl p-6 shadow-xl">
                                            <div className="flex justify-between items-center mb-6">
                                                <h3 className="text-xl font-serif font-bold text-gold-400">Packs de Jeu</h3>
                                                <div className="flex gap-2">
                                                    <input
                                                        type="file"
                                                        ref={fileInputRef}
                                                        onChange={handleImportPack}
                                                        accept=".json"
                                                        className="hidden"
                                                    />
                                                    <button
                                                        onClick={() => setShowImportHelp(true)}
                                                        className="w-8 h-8 rounded-full border border-navy-600 text-navy-400 hover:text-gold-400 hover:border-gold-500 flex items-center justify-center transition-colors font-serif font-bold"
                                                        title="Aide Format Import"
                                                    >
                                                        ?
                                                    </button>
                                                    <button
                                                        onClick={() => fileInputRef.current?.click()}
                                                        className="px-3 py-1 text-sm border border-navy-600 text-cream-200 hover:border-gold-500 hover:text-gold-400 rounded transition-colors flex items-center gap-1"
                                                    >
                                                        {isImporting ? <span className="animate-spin">↻</span> : '📥'} Import
                                                    </button>
                                                    <button
                                                        onClick={() => setIsEditingPack(true)}
                                                        className="btn-vintage px-3 py-1 text-sm"
                                                    >
                                                        + Nouveau
                                                    </button>
                                                </div>
                                            </div>

                                            {isEditingPack && (
                                                <form onSubmit={handleCreateOrUpdatePack} className="mb-6 bg-navy-800 p-4 rounded-lg border border-gold-500/30">
                                                    <div className="space-y-3">
                                                        <h4 className="text-gold-400 font-bold mb-2">{editingPackId ? 'Modifier le Pack' : 'Nouveau Pack'}</h4>
                                                        <input
                                                            type="text"
                                                            placeholder="Nom du pack"
                                                            value={newPackData.name}
                                                            onChange={e => setNewPackData({ ...newPackData, name: e.target.value })}
                                                            className="w-full bg-navy-900 border border-navy-600 rounded px-3 py-2 text-white"
                                                            required
                                                        />
                                                        <textarea
                                                            placeholder="Description"
                                                            value={newPackData.description}
                                                            onChange={e => setNewPackData({ ...newPackData, description: e.target.value })}
                                                            className="w-full bg-navy-900 border border-navy-600 rounded px-3 py-2 text-white h-20"
                                                            required
                                                        />
                                                        <label className="flex items-center gap-2 text-sm text-cream-200">
                                                            <input
                                                                type="checkbox"
                                                                checked={newPackData.isAdult}
                                                                onChange={e => setNewPackData({ ...newPackData, isAdult: e.target.checked })}
                                                                className="rounded border-navy-600 bg-navy-900 text-gold-500"
                                                            />
                                                            Contenu Adulte (18+)
                                                        </label>
                                                        <div className="flex justify-end gap-2">
                                                            <button
                                                                type="button"
                                                                onClick={() => { setIsEditingPack(false); setEditingPackId(null); }}
                                                                className="text-sm text-navy-400 hover:text-white"
                                                            >
                                                                Annuler
                                                            </button>
                                                            <button type="submit" className="text-sm text-gold-400 font-bold hover:text-gold-300">
                                                                {editingPackId ? 'Mettre à jour' : 'Créer'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </form>
                                            )}

                                            <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                                                <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
                                                    <button
                                                        onClick={() => setPackFilter('ALL')}
                                                        className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${packFilter === 'ALL' ? 'bg-gold-500 text-navy-900' : 'bg-navy-800 text-navy-400 hover:text-cream-100'}`}
                                                    >
                                                        Tous
                                                    </button>
                                                    <button
                                                        onClick={() => setPackFilter('OFFICIAL')}
                                                        className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${packFilter === 'OFFICIAL' ? 'bg-gold-500 text-navy-900' : 'bg-navy-800 text-navy-400 hover:text-cream-100'}`}
                                                    >
                                                        Officiels
                                                    </button>
                                                    <button
                                                        onClick={() => setPackFilter('COMMUNITY')}
                                                        className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${packFilter === 'COMMUNITY' ? 'bg-gold-500 text-navy-900' : 'bg-navy-800 text-navy-400 hover:text-cream-100'}`}
                                                    >
                                                        Communauté
                                                    </button>
                                                </div>

                                                {packs
                                                    .filter(p => {
                                                        if (packFilter === 'OFFICIAL') return p.isOfficial;
                                                        if (packFilter === 'COMMUNITY') return !p.isOfficial;
                                                        return true;
                                                    })
                                                    .map(pack => (
                                                        <div
                                                            key={pack._id}
                                                            onClick={() => setSelectedPack(pack)}
                                                            className={`p-4 rounded-lg border cursor-pointer transition-all ${selectedPack?._id === pack._id
                                                                ? 'bg-navy-800 border-gold-500 shadow-lg'
                                                                : 'bg-navy-950/50 border-navy-800 hover:border-gold-500/50'
                                                                }`}
                                                        >
                                                            <div className="flex justify-between items-start mb-2">
                                                                <div className="flex flex-col">
                                                                    <h4 className={`font-bold ${selectedPack?._id === pack._id ? 'text-gold-400' : 'text-cream-100'}`}>
                                                                        {pack.name}
                                                                    </h4>
                                                                    {pack.ownerId ? (
                                                                        <span className="text-[10px] text-blue-400 flex items-center gap-1">
                                                                            👤 {pack.ownerId.username}
                                                                        </span>
                                                                    ) : (
                                                                        <span className="text-[10px] text-gold-600/70 flex items-center gap-1">
                                                                            ⭐ Officiel
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="flex gap-1 items-center">
                                                                    {pack.isAdult && <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded border border-red-500/30">18+</span>}
                                                                    {!pack.isOfficial && (
                                                                        <button
                                                                            onClick={(e) => { e.stopPropagation(); handlePromotePack(pack._id); }}
                                                                            className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded border border-purple-500/30 hover:bg-purple-500/40 transition-colors"
                                                                            title="Promouvoir en Officiel"
                                                                        >
                                                                            ⭐ Promouvoir
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <p className="text-xs text-navy-300 mb-3 line-clamp-2">{pack.description}</p>
                                                            <div className="flex justify-between items-center text-xs">
                                                                <button
                                                                    onClick={(e) => { e.stopPropagation(); handleTogglePackActive(pack); }}
                                                                    className={`px-2 py-1 rounded ${pack.active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-navy-700 text-navy-400'}`}
                                                                >
                                                                    {pack.active ? 'Actif' : 'Inactif'}
                                                                </button>
                                                                <button
                                                                    onClick={(e) => { e.stopPropagation(); handleEditPack(pack); }}
                                                                    className="text-blue-400 hover:text-blue-300"
                                                                >
                                                                    Éditer
                                                                </button>
                                                                <button
                                                                    onClick={(e) => { e.stopPropagation(); handleExportPack(pack._id, pack.name); }}
                                                                    className="text-gold-600 hover:text-gold-400"
                                                                    title="Exporter en JSON"
                                                                >
                                                                    📤
                                                                </button>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleDeletePack(pack._id);
                                                                    }}
                                                                    className="text-red-400 hover:text-red-300"
                                                                >
                                                                    Supprimer
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right Column: Pack Details (Templates & Threads) */}
                                    <div className="lg:col-span-2 space-y-6">
                                        {selectedPack ? (
                                            <>
                                                {/* Templates Section */}
                                                <div className="bg-navy-900 border border-navy-700 rounded-xl p-6 shadow-xl">
                                                    <h3 className="text-xl font-serif font-bold text-cream-100 mb-4 flex items-center gap-2">
                                                        <span className="text-gold-500">📝</span> Templates ({packTemplates.length})
                                                    </h3>
                                                    <form onSubmit={handleCreateOrUpdateTemplate} className="flex flex-col gap-4 mb-4">
                                                        <div className="flex gap-2">
                                                            <input
                                                                type="text"
                                                                value={newTemplateText}
                                                                onChange={e => setNewTemplateText(e.target.value)}
                                                                placeholder="Question / Texte (Ex: Qui a volé l'orange ?)"
                                                                className="flex-1 bg-navy-950 border border-navy-700 rounded px-4 py-2 text-white focus:border-gold-500 outline-none"
                                                            />
                                                            <input
                                                                type="text"
                                                                value={newTemplateResponse}
                                                                onChange={e => setNewTemplateResponse(e.target.value)}
                                                                placeholder={isMCQMode ? "Bonne réponse (doit être une des options)" : "Réponse (Ex: Le marchand)"}
                                                                className="flex-1 bg-navy-950 border border-navy-700 rounded px-4 py-2 text-white focus:border-gold-500 outline-none"
                                                            />
                                                        </div>

                                                        {/* MCQ Options Section */}
                                                        <div className="bg-navy-950/50 p-3 rounded border border-navy-800">
                                                            <label className="flex items-center gap-2 mb-2 cursor-pointer w-fit">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isMCQMode}
                                                                    onChange={(e) => {
                                                                        setIsMCQMode(e.target.checked);
                                                                        if (e.target.checked && newTemplateOptions.length === 0) {
                                                                            setNewTemplateOptions(['', '', '']);
                                                                        }
                                                                    }}
                                                                    className="rounded border-navy-600 bg-navy-900 text-gold-500"
                                                                />
                                                                <span className={`text-sm font-bold ${isMCQMode ? 'text-gold-400' : 'text-navy-400'}`}>Mode QCM</span>
                                                            </label>

                                                            {isMCQMode && (
                                                                <div className="space-y-2 pl-6 border-l-2 border-navy-800">
                                                                    <p className="text-xs text-navy-400 mb-2">Entrez les options possibles. Assurez-vous que la "Réponse" ci-dessus correspond exactement à l'une d'elles.</p>
                                                                    {newTemplateOptions.map((opt, idx) => (
                                                                        <div key={idx} className="flex gap-2 items-center">
                                                                            <span className="text-navy-500 text-xs font-mono">{idx + 1}.</span>
                                                                            <input
                                                                                type="text"
                                                                                value={opt}
                                                                                onChange={(e) => {
                                                                                    const newOpts = [...newTemplateOptions];
                                                                                    newOpts[idx] = e.target.value;
                                                                                    setNewTemplateOptions(newOpts);
                                                                                }}
                                                                                placeholder={`Option ${idx + 1}`}
                                                                                className="flex-1 bg-navy-900 border border-navy-700 rounded px-3 py-1 text-sm text-cream-200 outline-none focus:border-blue-500"
                                                                            />
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    if (opt === newTemplateResponse) setNewTemplateResponse('');
                                                                                    const newOpts = newTemplateOptions.filter((_, i) => i !== idx);
                                                                                    setNewTemplateOptions(newOpts);
                                                                                }}
                                                                                className="text-red-500 hover:text-red-400 px-2"
                                                                                disabled={newTemplateOptions.length <= 2}
                                                                            >
                                                                                ×
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => setNewTemplateResponse(opt)}
                                                                                className={`text-xs px-2 py-0.5 rounded border ${newTemplateResponse === opt ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500' : 'border-transparent text-navy-500 hover:text-emerald-400'}`}
                                                                                title="Définir comme bonne réponse"
                                                                            >
                                                                                ✓ Correcte
                                                                            </button>
                                                                        </div>
                                                                    ))}
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setNewTemplateOptions([...newTemplateOptions, ''])}
                                                                        className="text-xs text-blue-400 hover:text-blue-300 mt-2 flex items-center gap-1"
                                                                    >
                                                                        + Ajouter une option
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div className="flex gap-2 justify-end">
                                                            {editingTemplateId && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setEditingTemplateId(null);
                                                                        setNewTemplateText('');
                                                                        setNewTemplateResponse('');
                                                                        setNewTemplateOptions([]);
                                                                        setIsMCQMode(false);
                                                                    }}
                                                                    className="px-3 py-2 text-navy-400 hover:text-white border border-navy-700 rounded"
                                                                >
                                                                    Annuler
                                                                </button>
                                                            )}
                                                            <button type="submit" className="btn-vintage px-4 py-2 text-sm">
                                                                {editingTemplateId ? 'Mettre à jour' : 'Ajouter'}
                                                            </button>
                                                        </div>
                                                    </form>
                                                    <div className="bg-black/20 rounded-lg p-2 max-h-[300px] overflow-y-auto space-y-1">
                                                        {packTemplates.map(tpl => (
                                                            <div key={tpl._id} className="group flex justify-between items-center p-2 hover:bg-white/5 rounded transition-colors">
                                                                <div className="flex flex-col">
                                                                    <span className="text-sm text-cream-200 font-mono">{tpl.text}</span>
                                                                    {tpl.response && (
                                                                        <span className="text-xs text-emerald-400 font-mono">➡ {tpl.response}</span>
                                                                    )}
                                                                </div>
                                                                <div className="flex gap-2">
                                                                    <button
                                                                        onClick={() => handleEditTemplate(tpl)}
                                                                        className="text-blue-400 hover:text-blue-300 opacity-0 group-hover:opacity-100 transition-opacity"
                                                                    >
                                                                        ✏️
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleDeleteTemplate(tpl._id)}
                                                                        className="text-navy-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                                                                    >
                                                                        ×
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="h-full flex flex-col items-center justify-center text-navy-500 border-2 border-dashed border-navy-800 rounded-xl p-12">
                                                <span className="text-4xl mb-4">👈</span>
                                                <p>Sélectionnez un pack pour gérer son contenu</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* THEMES TAB */}
                        {activeTab === 'THEMES' && (
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                {/* Themes List (Left Column) */}
                                <div className="bg-navy-900 border border-navy-700 rounded-xl p-6 shadow-xl h-fit">
                                    <h2 className="text-2xl font-serif font-bold text-gold-400 mb-6 flex items-center gap-3">
                                        <span className="text-3xl">🎨</span> Thèmes
                                    </h2>

                                    <button
                                        onClick={() => {
                                            setThemeForm({
                                                name: '',
                                                isDefault: false,
                                                cssVariables: {
                                                    '--bg-app': '#1a0f0a',
                                                    '--bg-board': '#2c1810',
                                                    '--border-color': '#8d6e63',
                                                    '--accent-primary': '#d4af37',
                                                    '--text-main': '#fdfbf7',
                                                    '--token-self': '#22c55e',
                                                    '--token-opp': '#ef4444',
                                                    '--paper-light': '#fdfbf7',
                                                    '--paper-dark': '#e6dfcc',
                                                    '--ink-black': '#1a1a1a'
                                                }
                                            });
                                            setEditingThemeId(null);
                                            setIsEditingTheme(true);
                                        }}
                                        className="w-full btn-vintage mb-6 py-3 flex justify-center items-center gap-2"
                                    >
                                        <span className="text-xl">+</span> Nouveau Thème
                                    </button>

                                    <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
                                        {themes.map(theme => (
                                            <div
                                                key={theme._id}
                                                className="p-4 rounded-lg bg-navy-950 border border-navy-800 hover:border-gold-500/50 transition-all cursor-pointer group relative overflow-hidden"
                                                onClick={() => {
                                                    setThemeForm({
                                                        name: theme.name,
                                                        isDefault: theme.isDefault,
                                                        cssVariables: { ...theme.cssVariables }
                                                    });
                                                    setEditingThemeId(theme._id);
                                                    setIsEditingTheme(true);
                                                }}
                                            >
                                                <div className="flex justify-between items-start mb-2 relative z-10">
                                                    <div>
                                                        <h3 className="font-bold text-cream-100 flex items-center gap-2">
                                                            {theme.name}
                                                            {theme.isDefault && (
                                                                <span className="text-[10px] bg-gold-500/20 text-gold-400 px-2 py-0.5 rounded-full border border-gold-500/30">DÉFAUT</span>
                                                            )}
                                                        </h3>
                                                    </div>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteTheme(theme._id);
                                                        }}
                                                        className="text-navy-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity p-1"
                                                    >
                                                        Supprimer
                                                    </button>
                                                </div>

                                                {/* Preview Strip */}
                                                <div className="flex h-3 w-full rounded-full overflow-hidden mt-3 border border-white/10 relative z-10">
                                                    <div style={{ background: theme.cssVariables['--bg-app'] }} className="flex-1" />
                                                    <div style={{ background: theme.cssVariables['--bg-board'] }} className="flex-1" />
                                                    <div style={{ background: theme.cssVariables['--accent-primary'] }} className="flex-1" />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Theme Editor Form (Right Column) */}
                                <div className="lg:col-span-2">
                                    {isEditingTheme ? (
                                        <div className="bg-navy-900 border border-navy-700 rounded-xl p-8 shadow-xl relative overflow-hidden">
                                            {/* Background Preview */}
                                            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-gold-500/5 to-transparent rounded-bl-full pointer-events-none" />

                                            <h3 className="text-2xl font-serif font-bold text-cream-100 mb-8 flex items-center gap-2">
                                                {themeForm.name ? `Édition: ${themeForm.name}` : 'Nouveau Thème'}
                                            </h3>

                                            <form onSubmit={handleSaveTheme} className="space-y-8 relative z-10">
                                                {/* Meta Info */}
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                    <div>
                                                        <label className="block text-navy-300 text-sm font-bold mb-2">Nom du Thème</label>
                                                        <input
                                                            type="text"
                                                            value={themeForm.name}
                                                            onChange={e => setThemeForm({ ...themeForm, name: e.target.value })}
                                                            className="w-full bg-navy-950 border border-navy-700 rounded px-4 py-3 text-white focus:border-gold-500 outline-none"
                                                            placeholder="Ex: Cyberpunk, Noir, etc."
                                                            required
                                                        />
                                                    </div>
                                                    <div className="flex items-center pt-6">
                                                        <label className="flex items-center gap-3 cursor-pointer group">
                                                            <div className={`w-6 h-6 rounded border flex items-center justify-center transition-colors ${themeForm.isDefault ? 'bg-gold-500 border-gold-500' : 'border-navy-600 bg-navy-950'}`}>
                                                                {themeForm.isDefault && <span className="text-navy-900 font-bold">✓</span>}
                                                            </div>
                                                            <input
                                                                type="checkbox"
                                                                checked={themeForm.isDefault}
                                                                onChange={e => setThemeForm({ ...themeForm, isDefault: e.target.checked })}
                                                                className="hidden"
                                                            />
                                                            <span className="text-navy-300 group-hover:text-cream-100 transition-colors">Définir comme thème par défaut</span>
                                                        </label>
                                                    </div>
                                                </div>

                                                <div className="border-t border-navy-800 my-6" />

                                                {/* Color Pickers Grid */}
                                                <div>
                                                    <h4 className="text-gold-400 font-bold mb-4 flex items-center gap-2">
                                                        <span className="w-1 h-4 bg-gold-500 rounded-full"></span>
                                                        Palette de Couleurs
                                                    </h4>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                        {Object.entries(themeForm.cssVariables).map(([key, value]) => (
                                                            <div key={key} className="bg-navy-950/50 p-4 rounded-lg border border-navy-800 hover:border-navy-600 transition-colors">
                                                                <div className="flex justify-between items-center mb-2">
                                                                    <label className="text-xs font-mono text-navy-400">{key}</label>
                                                                    <span className="text-xs text-cream-200 font-mono bg-navy-900 px-2 py-0.5 rounded">{value}</span>
                                                                </div>
                                                                <div className="flex gap-3">
                                                                    <input
                                                                        type="color"
                                                                        value={value.startsWith('linear') ? '#000000' : value} // Fallback for gradients in color picker
                                                                        onChange={(e) => setThemeForm({
                                                                            ...themeForm,
                                                                            cssVariables: { ...themeForm.cssVariables, [key]: e.target.value }
                                                                        })}
                                                                        className="h-10 w-full cursor-pointer bg-transparent rounded-lg"
                                                                    />
                                                                    <input
                                                                        type="text"
                                                                        value={value}
                                                                        onChange={(e) => setThemeForm({
                                                                            ...themeForm,
                                                                            cssVariables: { ...themeForm.cssVariables, [key]: e.target.value }
                                                                        })}
                                                                        className="w-full bg-navy-900 border border-navy-700 rounded px-2 text-xs text-white focus:border-gold-500 outline-none"
                                                                        placeholder="#RRGGBB or gradient(...)"
                                                                    />
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                <div className="flex justify-end gap-4 pt-6">
                                                    <button
                                                        type="button"
                                                        onClick={() => { setIsEditingTheme(false); setEditingThemeId(null); }}
                                                        className="px-6 py-2 rounded border border-navy-600 text-navy-400 hover:text-white hover:border-white transition-colors"
                                                    >
                                                        Annuler
                                                    </button>
                                                    <button
                                                        type="submit"
                                                        className="btn-vintage px-8 py-3 font-bold text-lg shadow-gold-glow"
                                                    >
                                                        Sauvegarder le Thème
                                                    </button>
                                                </div>
                                            </form>
                                        </div>
                                    ) : (
                                        <div className="h-full flex flex-col items-center justify-center text-navy-500 border-2 border-dashed border-navy-800 rounded-xl p-12 bg-navy-900/30">
                                            <span className="text-6xl mb-6 opacity-20">🎨</span>
                                            <p className="text-xl font-medium">Sélectionnez ou créez un thème</p>
                                            <p className="text-sm mt-2 opacity-60">Personnalisez l'apparence complète du jeu</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {activeTab === 'VERSIONS' && (
                            <VersionManager />
                        )}

                        {activeTab === 'TICKETS' && (
                            <div className="bg-navy-900 border border-navy-700 rounded-xl overflow-hidden shadow-xl">
                                <div className="p-6 border-b border-navy-800 flex justify-between items-center bg-navy-950">
                                    <h3 className="text-xl font-serif font-bold text-gold-400">Tickets Support ({tickets.length})</h3>
                                    <button onClick={fetchTickets} className="text-sm text-navy-400 hover:text-gold-400 border border-navy-700 px-3 py-1 rounded hover:border-gold-500 transition-all">
                                        Rafraîchir
                                    </button>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left">
                                        <thead className="bg-navy-950 text-navy-400 uppercase text-xs tracking-wider">
                                            <tr>
                                                <th className="px-6 py-4 font-medium">Sujet</th>
                                                <th className="px-6 py-4 font-medium">Statut</th>
                                                <th className="px-6 py-4 font-medium">Sévérité</th>
                                                <th className="px-6 py-4 font-medium">Auteur</th>
                                                <th className="px-6 py-4 font-medium">Date</th>
                                                <th className="px-6 py-4 font-medium">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-navy-800">
                                            {tickets.length === 0 ? (
                                                <tr>
                                                    <td colSpan={6} className="px-6 py-8 text-center text-navy-500 italic">Aucun ticket trouvé.</td>
                                                </tr>
                                            ) : (
                                                tickets.map(ticket => (
                                                    <tr key={ticket.id} className="hover:bg-navy-800/50 transition-colors">
                                                        <td className="px-6 py-4 font-bold text-cream-100">
                                                            {ticket.title}
                                                            <div className="text-xs text-navy-400 font-normal mt-1">{ticket.category}</div>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <span className={`px-2 py-1 rounded text-xs font-bold uppercase
                                                                ${ticket.status === 'open' ? 'bg-blue-500/20 text-blue-400' :
                                                                    ticket.status === 'in-progress' ? 'bg-amber-500/20 text-amber-400' :
                                                                        ticket.status === 'resolved' ? 'bg-emerald-500/20 text-emerald-400' :
                                                                            'bg-navy-700 text-navy-400'}`}>
                                                                {ticket.status}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <span className={`px-2 py-1 rounded text-xs font-bold uppercase
                                                                ${ticket.severity === 'critical' ? 'bg-red-500/20 text-red-500 animate-pulse' :
                                                                    ticket.severity === 'high' ? 'bg-orange-500/20 text-orange-400' :
                                                                        ticket.severity === 'medium' ? 'bg-yellow-500/10 text-yellow-400' :
                                                                            'text-navy-300'}`}>
                                                                {ticket.severity}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-cream-200">
                                                            {typeof ticket.reportedBy === 'string' ? ticket.reportedBy : ticket.reportedBy?.username || 'Anonyme'}
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-navy-400">
                                                            {new Date(ticket.createdAt).toLocaleDateString()}
                                                        </td>
                                                        <td className="px-6 py-4 flex gap-2">
                                                            {ticket.status !== 'resolved' && (
                                                                <button
                                                                    onClick={() => handleUpdateTicket(ticket.id, { status: 'resolved' })}
                                                                    className="text-xs border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 px-2 py-1 rounded transition-colors"
                                                                >
                                                                    ✓ Résoudre
                                                                </button>
                                                            )}
                                                            {ticket.status === 'open' && (
                                                                <button
                                                                    onClick={() => handleUpdateTicket(ticket.id, { status: 'in-progress' })}
                                                                    className="text-xs border border-amber-500/30 text-amber-400 hover:bg-amber-500/10 px-2 py-1 rounded transition-colors"
                                                                >
                                                                    ▶ Traiter
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </main>
                </div>
            </div>
            {showImportHelp && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <div className="bg-navy-900 border border-gold-500/30 rounded-xl max-w-lg w-full p-6 shadow-2xl relative">
                        <button
                            onClick={() => setShowImportHelp(false)}
                            className="absolute top-4 right-4 text-navy-400 hover:text-white"
                        >
                            ✕
                        </button>
                        <h3 className="text-xl font-bold text-gold-400 mb-4">Format d'Import JSON</h3>
                        <p className="text-sm text-cream-200 mb-4">
                            Le fichier doit être un JSON valide respectant cette structure :
                        </p>
                        <pre className="bg-black/50 p-4 rounded text-xs font-mono text-emerald-400 overflow-x-auto border border-navy-800">
                            {`{
  "name": "Nom du Pack",
  "description": "Description...",
  "isAdult": false,
  "isOfficial": false,   // Optionnel (défaut: false)
  "active": true,        // Optionnel (défaut: false)
  "isPrivate": true,     // Optionnel (défaut: true)
  "templates": [
    {
      "text": "Question 1 ?",
      "response": "Réponse 1",
      "options": ["Réponse 1", "Mauvaise 1", "Mauvaise 2"] // Optionnel (pour QCM)
    }
  ]
}`}
                        </pre>
                        <div className="mt-6 flex justify-end">
                            <button
                                onClick={() => setShowImportHelp(false)}
                                className="btn-vintage px-6 py-2"
                            >
                                Compris
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </VintageLayout>
    );
};
